
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Star, MapPin, Wifi, Car, Coffee, Users, Search } from "lucide-react";
import { Badge } from "./ui/badge";

interface Accommodation {
  id: number;
  name: string;
  type: string;
  location: string;
  rating: number;
  price_range: string;
  image_url: string;
  amenities: string[];
  description: string;
  contact_info: {
    phone: string;
    email: string;
    website?: string;
  };
  gallery_images: string[];
  created_at: string;
}

const AffiliateAccommodations = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [selectedAccommodation, setSelectedAccommodation] = useState<Accommodation | null>(null);

  const { data: accommodations = [], isLoading } = useQuery({
    queryKey: ["accommodations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sns_accommodations")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching accommodations:", error);
        throw error;
      }

      return data as Accommodation[];
    },
  });

  const filteredAccommodations = accommodations.filter((acc) => {
    const matchesSearch = acc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         acc.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === "all" || acc.type === filterType;
    return matchesSearch && matchesType;
  });

  const amenityIcons = {
    wifi: <Wifi className="w-4 h-4" />,
    parking: <Car className="w-4 h-4" />,
    breakfast: <Coffee className="w-4 h-4" />,
    pool: <Users className="w-4 h-4" />,
  };

  if (isLoading) {
    return (
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-shell-dark">Our Partner Accommodations</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-200 h-48 rounded-lg mb-4"></div>
                <div className="h-6 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gray-50" id="accommodations">
      <div className="container mx-auto px-4">
        <h2 className="text-4xl font-bold text-center mb-4 text-shell-dark">
          Our Partner Accommodations
        </h2>
        <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
          Discover handpicked hotels, guesthouses, and resorts that offer exceptional experiences in the Maldives
        </p>

        <div className="flex flex-col md:flex-row gap-4 mb-8 max-w-2xl mx-auto">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search accommodations or locations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="md:w-48">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="resort">Resort</SelectItem>
              <SelectItem value="hotel">Hotel</SelectItem>
              <SelectItem value="guesthouse">Guesthouse</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredAccommodations.map((accommodation) => (
            <Card key={accommodation.id} className="overflow-hidden hover:shadow-lg transition-shadow">
              <div className="relative">
                <img
                  src={accommodation.image_url}
                  alt={accommodation.name}
                  className="w-full h-48 object-cover"
                />
                <Badge className="absolute top-2 right-2 bg-shell-primary">
                  {accommodation.type}
                </Badge>
              </div>
              <CardHeader>
                <CardTitle className="text-xl">{accommodation.name}</CardTitle>
                <div className="flex items-center gap-1 text-sm text-gray-600">
                  <MapPin className="w-4 h-4" />
                  {accommodation.location}
                </div>
                <div className="flex items-center gap-1">
                  {Array.from({ length: accommodation.rating }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                  <span className="text-sm text-gray-600 ml-1">
                    ({accommodation.rating}/5)
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4 line-clamp-2">{accommodation.description}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {accommodation.amenities.slice(0, 3).map((amenity) => (
                    <div key={amenity} className="flex items-center gap-1 text-xs bg-gray-100 px-2 py-1 rounded">
                      {amenityIcons[amenity as keyof typeof amenityIcons] || <Coffee className="w-3 h-3" />}
                      {amenity}
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-shell-primary">
                    {accommodation.price_range}
                  </span>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        onClick={() => setSelectedAccommodation(accommodation)}
                      >
                        View Details
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>{accommodation.name}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <img
                          src={accommodation.image_url}
                          alt={accommodation.name}
                          className="w-full h-64 object-cover rounded-lg"
                        />
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="font-semibold mb-2">Location</h4>
                            <p className="text-gray-600 flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {accommodation.location}
                            </p>
                          </div>
                          <div>
                            <h4 className="font-semibold mb-2">Price Range</h4>
                            <p className="text-shell-primary font-semibold">
                              {accommodation.price_range}
                            </p>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">Description</h4>
                          <p className="text-gray-600">{accommodation.description}</p>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">Amenities</h4>
                          <div className="flex flex-wrap gap-2">
                            {accommodation.amenities.map((amenity) => (
                              <Badge key={amenity} variant="secondary">
                                {amenity}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">Contact Information</h4>
                          <div className="space-y-1 text-sm">
                            <p>Phone: {accommodation.contact_info.phone}</p>
                            <p>Email: {accommodation.contact_info.email}</p>
                            {accommodation.contact_info.website && (
                              <p>
                                Website: 
                                <a 
                                  href={accommodation.contact_info.website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-shell-primary ml-1 hover:underline"
                                >
                                  {accommodation.contact_info.website}
                                </a>
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredAccommodations.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600">No accommodations found matching your criteria.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default AffiliateAccommodations;
